import { motion } from "framer-motion";
import { Settings, CheckCircle, Link } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { DoulaListing } from "./types";

interface IntegrationsCardProps {
	listing: DoulaListing | null;
	onGoogleCalendarConnect: () => void;
	onStripeConnect: () => void;
}

export default function IntegrationsCard({
	listing,
	onGoogleCalendarConnect,
	onStripeConnect,
}: IntegrationsCardProps) {
	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ delay: 0.3 }}
			className="mt-8"
		>
			<Card className="glass-card border-0 shadow-lg">
				<CardHeader>
					<CardTitle className="text-xl font-karla flex items-center">
						<Settings className="h-5 w-5 mr-2 text-newTheme-orange" />
						Integrations & Connections
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						{/* Google Calendar */}
						<div className="flex items-center justify-between p-4 bg-white/80 rounded-lg border border-gray-200">
							<div className="flex items-center space-x-4">
								<svg className="h-auto w-8 object-contain" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
									<path fill="#fff" d="m152.632 47.368-47.368-5.263-57.895 5.263L42.105 100l5.263 52.632L100 159.211l52.632-6.579 5.263-53.947z" />
									<path fill="#1a73e8" d="M68.961 129.026c-3.934-2.658-6.658-6.539-8.145-11.671l9.132-3.763q1.243 4.737 4.342 7.342c2.053 1.737 4.553 2.592 7.474 2.592q4.48 0 7.697-2.724c3.217-2.724 3.224-4.132 3.224-6.934q0-4.303-3.395-7.026c-3.395-2.723-5.105-2.724-8.5-2.724h-5.276v-9.039h4.736q4.382 0 7.382-2.368c2-1.579 3-3.737 3-6.487 0-2.447-.895-4.395-2.684-5.855s-4.053-2.197-6.803-2.197c-2.684 0-4.816.711-6.395 2.145s-2.724 3.197-3.447 5.276l-9.039-3.763c1.197-3.395 3.395-6.395 6.618-8.987s7.342-3.895 12.342-3.895c3.697 0 7.026.711 9.974 2.145 2.947 1.434 5.263 3.421 6.934 5.947 1.671 2.539 2.5 5.382 2.5 8.539 0 3.224-.776 5.947-2.329 8.184s-3.461 3.947-5.724 5.145v.539a17.4 17.4 0 0 1 7.342 5.724c1.908 2.566 2.868 5.632 2.868 9.211s-.908 6.776-2.724 9.579-4.329 5.013-7.513 6.618C89.355 132.184 85.763 133 81.776 133c-4.618.013-8.881-1.316-12.815-3.974M125 83.711l-9.974 7.25-5.013-7.605L128 70.382h6.895v61.197H125z" />
									<path fill="#ea4335" d="M152.632 200 200 152.632l-23.684-10.526-23.684 10.526-10.526 23.684z" />
									<path fill="#34a853" d="M36.842 176.316 47.368 200h105.263v-47.368H47.368z" />
									<path fill="#4285f4" d="M15.789 0C7.066 0 0 7.066 0 15.789v136.842l23.684 10.526 23.684-10.526V47.368h105.263l10.526-23.684L152.632 0z" />
									<path fill="#188038" d="M0 152.632v31.579C0 192.935 7.066 200 15.789 200h31.579v-47.368z" />
									<path fill="#fbbc04" d="M152.632 47.368v105.263H200V47.368l-23.684-10.526z" />
									<path fill="#1967d2" d="M200 47.368V15.789C200 7.065 192.934 0 184.211 0h-31.579v47.368z" />
								</svg>
								<div>
									<h4 className="font-semibold text-newTheme-grey font-karla">
										Google Calendar
									</h4>
									<p className="text-sm text-gray-600 font-karla">
										{listing?.google_calendar_id
											? "Connected"
											: "Not connected"}
									</p>
									{listing?.google_calendar_id && (
										<p className="text-xs text-gray-500 font-karla">
											ID: {listing.google_calendar_id}
										</p>
									)}
								</div>
							</div>
							<Button
								variant="outline"
								size="sm"
								onClick={onGoogleCalendarConnect}
								disabled={!!listing?.google_calendar_id}
								className="bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow border-0"
							>
								{listing?.google_calendar_id ? (
									<>
										<CheckCircle className="h-4 w-4 mr-2" />
										Connected
									</>
								) : (
									<>
										<Link className="h-4 w-4 mr-2" />
										Connect
									</>
								)}
							</Button>
						</div>

						{/* Stripe Connect */}
						<div className="flex items-center justify-between p-4 bg-white/80 rounded-lg border border-gray-200">
							<div className="flex items-center space-x-4">
								<img
									src="https://asxufbnomjttcikjcjfa.supabase.co/storage/v1/object/public/marketing/stripe_logo.jpg"
									alt="Stripe"
									className="h-8 w-8"
								/>
								<div>
									<h4 className="font-semibold text-newTheme-grey font-karla">
										Stripe Connect
									</h4>
									<p className="text-sm text-gray-600 font-karla">
										{listing?.stripe_connect_account_id
											? "Connected"
											: "Not connected"}
									</p>
									{listing?.stripe_connect_account_id && (
										<p className="text-xs text-gray-500 font-karla">
											Account: {listing.stripe_connect_account_id}
										</p>
									)}
								</div>
							</div>
							<Button
								variant="outline"
								size="sm"
								onClick={onStripeConnect}
								className="border-newTheme-lightGrey text-black hover:bg-gray-50 shadow-btn-shadow hover:text-black"
								disabled={!!listing?.stripe_connect_account_id}
							>
								{listing?.stripe_connect_account_id ? (
									<>
										<CheckCircle className="h-4 w-4 mr-2" />
										Connected
									</>
								) : (
									<>
										<Link className="h-4 w-4 mr-2" />
										Connect
									</>
								)}
							</Button>
						</div>
					</div>
				</CardContent>
			</Card>
		</motion.div>
	);
}
